import {useState, useMemo} from 'react';
import {OverlayTrigger, Tooltip} from 'react-bootstrap';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {NotificationManager} from 'react-notifications';

import Table from '../../../common/table/TableFunctional';
import DeleteModal from '../apartment_cashflow_table/DeleteModal';
import RefundModal from '../apartment_cashflow_table/RefundModal';
import {calcMonthsTextForSelectBox, getRowProps, shortenText} from '../../../common/shared/statelessMethods';
import { constants } from '../../../common/constants/constants';
import Refund from '../apartment_cashflow_table/Refund';
import Delete from '../apartment_cashflow_table/Delete';
import {hideLoader, showLoader} from '../../../store/common/slice';
import BllinkUtils from '../../../common/utils/utils';
import {StopPlansModal} from "../yearlyByMonths/StopPlansModal";
import {StopActionIcon} from "./StopActionIcon";
import {isAdminSelector} from "../../../store/auth/selectors";

const StringUtils = BllinkUtils.Strings;
const BllinkTime = BllinkUtils.BllinkTime;

export default function FullReport({monthlyData, apartmentNumber, buildingID, client, filters, onPaymentStop}) {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const isAdmin = useSelector(isAdminSelector);
    const [showRefundModal, setShowRefundModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [rowToHandle, setRowToHandle] = useState(null);
    const [deletePending, setDeletePending] = useState('one');

    const monthlyColumns = useMemo(
        () => [
            {
                Header: t('reports.billing_date'),
                accessor: 'PaymentDate',
                Cell: props => (<span>{BllinkTime.parseDate(props.value)}</span>),
            },
            {
                Header: t('reports.tenantName'),
                accessor: 'ClientName',
            },
            {
                Header: t('reports.total_to_pay'),
                accessor: 'Amount',
                Cell: props => (<span>{StringUtils.numberWithCommas(props.value)}</span>),
                className: 'number-cell',
            },
            {
                Header: t('reports.4_last_digits'),
                accessor: 'CardLast4Digits',
            },
            {
                Header: t('reports.ownership_type'),
                Cell: ({ row }) => {
                    const tenantTypeRaw = row.original.TenantType ? t(`reports.${row.original.TenantType}`) : '';
                    return <>{tenantTypeRaw}</>
                }
            },
            {
                Header: t('reports.payment_for'),
                id: 'paymentFor',
                Cell: ({row}) => {
                    const {months, oneTime} = row.original;
                    let text = '';
                    if (Array.isArray(months)) {
                        text = shortenText(calcMonthsTextForSelectBox(months));
                    }
                    if (oneTime) {
                        text = shortenText(oneTime.description);
                    }
                    return (
                        <OverlayTrigger
                            placement="top"
                            overlay={props => (
                                <Tooltip {...props}>
                                    {text?.originalComment}
                                </Tooltip>
                            )}
                        >
                            <span exceltext={text?.originalComment || text}>{text?.shortComment || text}</span>
                        </OverlayTrigger>
                    );
                }
            },
            {
                Header: t('reports.payment_comment'),
                accessor: 'Comment',
                Cell: props => {
                    const text = shortenText(props.value);
                    return (
                        <OverlayTrigger
                            placement="top"
                            overlay={props => (
                                <Tooltip {...props}>
                                    {text?.originalComment}
                                </Tooltip>
                            )}
                        >
                            <span exceltext={text?.originalComment || text}>{text?.shortComment || text}</span>
                        </OverlayTrigger>
                    );
                },
            },
            {
                Header: t('payments.confirmation_id'),
                accessor: 'invoiceDownloadLink',
            },
            {
                Header: t('payments.payment_method'),
                id: 'Method',
                accessor: 'translatedMethod',
            },
            {
                Header: t('reports.confirmation'),
                accessor: 'cartID',
                Cell: ({value}) => {
                    if (!value) return '';
                    const isNumber = Number.isInteger(value);
                    let url;

                    if (isNumber) {
                        url = `/managers/reports/confirmation-url/${buildingID}/${value}`;
                        return (<a href={url} onClick={e => handleTablesLinksClicked(e, url)}>{t('reports.confirmation')}</a>);

                    } else {
                        // bllink orders
                        url = `/${constants.pages.orderStatusPage}/${value}`
                        return (
                            <a
                                target="_blank"
                                rel="noopener noreferrer"
                                href={url}
                            >
                                {t('reports.confirmation')}
                            </a>
                        );
                    }
                }
            },
            {
                Header: t('reports.receipt'),
                id: 'receipt',
                Cell: ({row}) => {
                    const {isOfflinePayment, invoiceDownloadLink, PaymentState} = row.original;
                    if (isOfflinePayment) {
                        return <a className="disabled" href='/'>{t('reports.receipt')}</a>
                    }
                    if (invoiceDownloadLink) {
                        const url = `/managers/reports/invoice-url/${buildingID}/${invoiceDownloadLink}`;
                        // disable link and add tooltip in case payment is in status in progress
                        const link = PaymentState === constants.APIContract.paymentStates.IN_PROGRESS ?
                            (<OverlayTrigger
                                placement="top"
                                overlay={props => (
                                    <Tooltip {...props}>
                                        {t('reports.awaitingApproval')}
                                    </Tooltip>
                                )}
                            >
                                <div>{t('reports.receipt')}</div>
                            </OverlayTrigger>) :
                            <a href={url} onClick={e => handleTablesLinksClicked(e, url)}>{t('reports.receipt')}</a>
                        return link
                    }
                    return '';
                }
            },
            {
                Header: t('reports.actions'),
                Cell: ({ row }) => {
                    let deleteTooltipText = '';
                    const isNotEditableReasons = row.original?.reasons?.isNotEditable || [];
                    isNotEditableReasons.forEach(text => {
                        const key = `reports.reasons.${text}`;
                        deleteTooltipText += `${t(key)}<br/>`;
                    });

                    const getDelete = () => {
                        if ([constants.managerPaymentsOptions.creditCardsServerKey,
                            constants.managerPaymentsOptions.bankChargeKey,
                            constants.managerPaymentsOptions.bitKey,
                            ].includes(row.original.Method)) {
                            return null;
                        }
                        if (row.original.isEditable) {
                            return <Delete onClick={() => onDeleteIconClick(row)}/>;
                        } else {
                            return <Delete disabled tooltipText={deleteTooltipText}/>;
                        }
                    }
                    return (
                        <div className="actions-wrapper">
                            {/* adding !row.original.isOfflinePayment since the refund action is not relevant for offline. caused a bug  */}
                            {(row.original.isRefundable || row.original.isFutureCharge) && !row.original.isOfflinePayment && (
                                <Refund
                                    onClick={() => onRefundIconClick(row)}
                                    isFuture={row.original.isFutureCharge}
                                />
                            )}
                            {getDelete()}
                            { (row.original.isStoppableOnline) && (
                                <StopActionIcon onClick={() => handleStopOnlineClick(row)} />
                            ) }
                        </div>
                    );
                },
            },
        ],
        [monthlyData]
    );

    const [stopPlanContext, setStopPlanContext] = useState({ show: false });

    function handleStopOnlineClick(row) {
        const paymentPlanId = row.original.PaymentPlanId;
        setStopPlanContext({ show: true, buildingID, record: { apartmentNumber, paymentPlanId  }, year: 'all', paymentPlanId })
    }

    function handleStopOnlineHide(stopped, record) {
        setStopPlanContext({ show: false });
        if (stopped) {
            onPaymentStop(record);
        }
    }

    //refund modal
    const onRefundModalHide = () => setShowRefundModal(false);

    const onRefundIconClick = row => {
        setRowToHandle(row);
        setShowRefundModal(true);
    };

    //delete modal
    const onDeleteIconClick = row => {
        setRowToHandle(row);
        setShowDeleteModal(true);
    };

    const onDeleteModalHide = () => setShowDeleteModal(false);

    const handleRadioChange = e => {
        setDeletePending(e.target.value);
    };

    const deletePayment = async () => {
        dispatch(showLoader());
        let response;
        if (rowToHandle) {
            const query = `deleteAllPending=${deletePending === 'all'}&deleteAllNextPending=${deletePending === 'allNext'}`;
            response = await client.post(`/managers/delete-external-payments/${rowToHandle.original.PaymentId}?${query}`)
        }

        await onPaymentStop({ apartmentNumber });

        const updated = response?.paymentResult?.updated;
        if (updated && updated > 0) {
            NotificationManager.success(`${updated} ${t('reports.delete.notificationSuccess')}`, '', 5000);
        } else if (updated && updated === 0) {
            NotificationManager.warning(t('reports.delete.notificationNoUpdates'), '', 5000);
        } else {
            NotificationManager.error(t('reports.delete.notificationFailure'), '', 5000);
        }

        onDeleteModalHide();

        dispatch(hideLoader());
    };

    const handleTablesLinksClicked = async (e, url) => {
        e.preventDefault();

        dispatch(showLoader());
        const response = await client.get(url);
        dispatch(hideLoader());
        if (response) {
            console.log(`response is`, response);
            let win = window.open(response, '_blank');
            win && win.focus();
        }
    }

    return monthlyData && monthlyData.length > 0 ? (
        <>
            <Table
                buildingId={buildingID}
                className={'new-report'}
                columns={monthlyColumns}
                data={monthlyData}
                filters={filters}
                getRowProps={getRowProps}
                tableTitle={t('reports.apartment_cashflow_title', {apartmentNumber: apartmentNumber})}
            />

            <DeleteModal
                show={showDeleteModal}
                onHide={onDeleteModalHide}
                rowToDelete={rowToHandle}
                currentRadio={deletePending}
                onRadioChange={handleRadioChange}
                onDeleteClick={deletePayment}
            />

            <RefundModal
                show={showRefundModal}
                onHide={onRefundModalHide}
                paymentID={rowToHandle && rowToHandle.original.PaymentId}
                paymentPlanID={rowToHandle && rowToHandle.original.PaymentPlanId}
                buildingID={buildingID}
                client={client}
                isFuture={rowToHandle && rowToHandle.original.isFutureCharge}
            />

            <StopPlansModal onHide={handleStopOnlineHide} {...stopPlanContext} />
        </>
    ) : (
        <h2 className='text-center'>{t('reports.empty_table')}</h2>
    );
}
